import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  getBills,
  getConsumption,
  getUser,
} from "features/portal/services/portalApi";
import { useTour } from "hooks/useTour";

import type { PortalViewModel } from "features/portal/types";

function mapBillsToSlips(data: unknown): any {
  return (data as any[])?.reduce((previousValue: any[], currentValue: any) => {
    const newValue = { invoice: currentValue, installment: currentValue };
    newValue.installment.paymentTypeBoleto = true;
    previousValue.push(newValue);
    return previousValue;
  }, []);
}

export function usePortal(): PortalViewModel {
  const [billsData, setBillsData] = useState<any[]>([]);
  const [spendingData, setSpendingData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFirstAccess, setIsFirstAccess] = useState<boolean | null>(null);
  const selectedUnit = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer.selectedUnit
  );

  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const clientUserId =
    localStorage.getItem("@UltragazEmpresarial:userId") || undefined;

  useEffect(() => {
    loadApis();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const userId = localStorage.getItem("@UltragazEmpresarial:userId");
        if (!userId) {
          setIsFirstAccess(false);
          return;
        }
        const data = (await getUser(userId)) as { isFirstLogin?: boolean };
        setIsFirstAccess(Boolean(data?.isFirstLogin));
      } catch {
        setIsFirstAccess(false);
      }
    })();
  }, []);

  useEffect(() => {
    setTourType("home");
    setSteps(10);
  }, [showTour, setTourType, setSteps]);

  useEffect(() => {
    loadApis();
  }, [selectedUnit]);

  const loadApis = async (): Promise<void> => {
    setLoading(true);
    getInvoices();
    getSpendings();
    setLoading(false);
  };

  const getSpendings = async (): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;

    if (!instanceId) return;

    const endDate = moment()
      .add(1, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    const beginDate = moment(endDate)
      .subtract(12, "month")
      .endOf("month")
      .format("YYYY-MM-DD");

    const data = (await getConsumption({
      document,
      unitId: instanceId,
      beginDate,
      endDate,
    })) as any[];

    setSpendingData(data?.splice(0, 2));
  };

  const getInvoices = async (): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;

    const data = await getBills({ document, unitId: instanceId });
    setBillsData(mapBillsToSlips(data));
  };

  const bill = useMemo(() => billsData[0], [billsData]);

  const bills = useMemo(() => billsData?.slice(1, 5), [billsData]);

  const spending = useMemo(() => {
    const newData = spendingData
      ?.map((item, index) => {
        let monthValueColor = "#000fff";

        if (index === 0) {
          monthValueColor = "#97F8FB";
        }

        return {
          month: moment(
            item?.measurementDate?.replaceAll("-", "/"),
            "YYYY-MM-DD"
          ).format(""),
          monthValue: item?.kgConsumption.replace(",", "."),
          monthValueColor,
        };
      })
      .splice(0, 2)
      .reverse();
    return newData;
  }, [spendingData]);

  return {
    bill,
    bills,
    clientUserId,
    isFirstAccess,
    loading,
    selectedUnit,
    showTour,
    spending,
    tourStep,
    tourType,
  };
}
